import { Injectable } from '@angular/core';
import { catchError, last, lastValueFrom, map, of, Subject, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppInfos } from '../../app.models';
import { compareVersions } from 'compare-versions';
import { CluConfirmDialogService } from '../../utils/clu-confirm-dialog/clu-confirm-dialog.service';
import { HandleError } from 'src/app/utils/network-error-handler';
import { getCurrentWindow } from '@tauri-apps/api/window';
import { exit } from '@tauri-apps/plugin-process';

@Injectable({ providedIn: 'root' })
export class TauriService {

    readonly isTauri = !!(window as any).__TAURI__;
    tauriEndpointUrl = 'http://localhost:51900';

    cluTicketEnteteObservable$ = new Subject<any>();

    appUpdateInfos: any;
    appUpdateDownloadProgress = 0;

    constructor(
        private _httpClient: HttpClient,
        private cluConfirmDialogService: CluConfirmDialogService
    ) {}

    async minimizeApp() {
      await getCurrentWindow().minimize();
    }

    async quitApp() {
      await exit();
    }

    async checkForUpdates(appInfos: AppInfos) {
      try {
        const updateInfos = await lastValueFrom(this._httpClient.get<any>(`https://res.leseditionscauris.com/cauris-linkup-workspace-app-updater.json`));
        if (updateInfos && updateInfos.version && compareVersions(updateInfos.version, appInfos.APP_VERSION_ID) > 0) {
          this.appUpdateInfos = {
            APP_VERSION_ID: updateInfos.version
          };
          const confirmUpdates = await this.cluConfirmDialogService.showConfirmDialog('Mise à jour disponible', 'Une nouvelle version de l\'application est disponible. Voulez-vous l\'installer ?');
          return confirmUpdates;
        }
      } catch (error) {
        console.error('Erreur lors de la vérification des mises à jour :', error);
      }

      return false;
    }

    async getAppVersion(): Promise<AppInfos> {
        return lastValueFrom(this._httpClient.get<AppInfos>(`${this.tauriEndpointUrl}/api/app/get-version`));
    }

    doCleanCluDataItemsAction() {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/clean-datas`, {}));
    }

    doRunCaskdeskZClosingAction(payload: { MD_CAISSE: string, MD_CAISSE_SESSION: string }) {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/run-zclosing`, payload));
    }

    doRunCashdeskSyncDownAction(syncParams: any) {
      return this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/sync-down`, syncParams).pipe(catchError(error => HandleError(error)));
    }


    // #region ************************************* TICK_CURRENTPIECE *************************************
    doGetCluTickCurrentPiecesAction(queryParams: QueryParams_TickCurrentPiece): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickcurrentpieces/get`, queryParams));
    }

    doFindOneTickCurrentPieceAction(queryParams: QueryParams_TickCurrentPiece): Promise<any> {
      return this.doGetCluTickCurrentPiecesAction(Object.assign({ PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }, queryParams)).then(results => results.length > 0 ? results[0] : null);
    }

    doCreateTickCurrentPieceAction(tickCurrentPieceItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickcurrentpieces/create`, tickCurrentPieceItem));
    }

    doUpdateCluTickCurrentPieceAction(id: string, pdeviceItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickcurrentpieces/update/${id}`, pdeviceItem));
    }

    doDeleteCluTickCurrentPiecesAction(queryParams: QueryParams_TickCurrentPiece): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickcurrentpieces/delete`, queryParams));
    }


    // #region ************************************* PDEVICE *************************************
    doGetPDeviceItemsAction(queryParams: QueryParams_PDevice): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/pdevice/get`, queryParams));
    }

    doFindOneDeviceItemAction(queryParams: QueryParams_PDevice): Promise<any> {
      return this.doGetPDeviceItemsAction(Object.assign({ PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }, queryParams)).then(results => results.length > 0 ? results[0] : null);
    }

    doGetPDeviceItemByCaissegidAction(caissegid: string): Promise<any> {
        return lastValueFrom(this._httpClient.get(`${this.tauriEndpointUrl}/api/cashdesk/pdevice/find/${caissegid}`).pipe(catchError((error) => {
          return of(null);
        })));
        /*.pipe(catchError(error => HandleError(error))))*/;
    }

    doSyncPDeviceItemAction(pdeviceItems: any[]): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/pdevice/sync`, pdeviceItems).pipe(catchError(error => HandleError(error))));
    }

    doUpdatePDeviceItemAction(id: number, pdeviceItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/pdevice/update/${id}`, pdeviceItem).pipe(catchError(error => HandleError(error))));
    }

    doDeleteAllPDeviceItemsAction(): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/pdevice/delete_all`, {}).pipe(catchError(error => HandleError(error))));
    }

    // #region ************************************* CLU DUPLICATAS *************************************
    private _getCluTickDuplicataFiltersDto(queryParams: QueryParams_CluTIckDuplicata): any {
        const filtersDto: any = {};
        Object.keys(queryParams).forEach(key => {
            if (queryParams[key as keyof QueryParams_CluTIckDuplicata] !== undefined) {
                if (key === 'DUP_PIECE_INTERNAL' && typeof queryParams.DUP_PIECE_INTERNAL === 'string') {
                    filtersDto.DUP_PIECE_INTERNAL = { operator: 'eq', value: queryParams.DUP_PIECE_INTERNAL };
                } else {
                    filtersDto[key] = queryParams[key as keyof QueryParams_CluTIckDuplicata];
                }
            }
        });
        return filtersDto;
    }
    doGetDuplicataItemsAction(queryParams: QueryParams_CluTIckDuplicata): Promise<any[]> {
        const filtersDto = this._getCluTickDuplicataFiltersDto(queryParams);
        return lastValueFrom(this._httpClient.post<any[]>(`${this.tauriEndpointUrl}/api/cashdesk/duplicatas/get`, filtersDto).pipe(catchError(error => HandleError(error))));
    }

    doGetFirstDuplicataItemAction(queryParams: QueryParams_CluTIckDuplicata): Promise<any> {
        return this.doGetDuplicataItemsAction(Object.assign({ PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }, queryParams)).then(results => results.length > 0 ? results[0] : null, error => HandleError(error));
    }

    doCreateDuplicataItemAction(duplicataItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/duplicatas/create`, duplicataItem).pipe(catchError(error => HandleError(error))));
    }

    doSaveDuplicataItemAction(duplicataItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/duplicatas/update/${duplicataItem.DUP_PIECE_INTERNAL}`, duplicataItem).pipe(catchError(error => HandleError(error))));
    }

    // #region ************************************* CLU TICKETS *************************************
    doGetCluTickItemsAction(queryParams: QueryParams_CluTickEntete): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets/get`, queryParams).pipe(catchError(error => HandleError(error))));
    }

    doGetCluTickItemAction(doPieceInternal: string): Promise<any> {
        return lastValueFrom(this._httpClient.get(`${this.tauriEndpointUrl}/api/cashdesk/tickets/find/${doPieceInternal}`).pipe(catchError(error => HandleError(error))));
    }

    doCreateCluTickItemAction(ticketItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets/create`, ticketItem).pipe(map((item: any) => {
            this.cluTicketEnteteObservable$.next(item.ENTETE);
            return item;
        }, catchError(error => HandleError(error)))));
    }

    doSaveCluTickItemAction(ticketItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets/update/${ticketItem.ENTETE.DO_PIECE_INTERNAL}`, ticketItem).pipe(map((item: any) => {
            this.cluTicketEnteteObservable$.next(item.ENTETE);
            return item;
        }, catchError(error => HandleError(error)))));
    }
    // #endregion ************************************* CLU TICKETS - END *************************************

    // #region ************************************* CLU TICKETS ENTETES *************************************
    private _getCluTickEnteteFiltersDto(queryParams: QueryParams_CluTickEntete): any {
        const filtersDto: any = {};
        Object.keys(queryParams).forEach(key => {
            if (queryParams[key as keyof QueryParams_CluTickEntete] !== undefined) {
                if (key === 'DO_PIECE_INTERNAL' && typeof queryParams.DO_PIECE_INTERNAL === 'string') {
                    filtersDto.DO_PIECE_INTERNAL = { operator: 'eq', value: queryParams.DO_PIECE_INTERNAL };
                } else {
                    filtersDto[key] = queryParams[key as keyof QueryParams_CluTickEntete];
                }
            }
        });
        return filtersDto;
    }

    doGetCluTickEntetesAction(queryParams: QueryParams_CluTickEntete): Promise<any[]> {
        const filtersDto = this._getCluTickEnteteFiltersDto(queryParams);
        return lastValueFrom(this._httpClient.post<any[]>(`${this.tauriEndpointUrl}/api/cashdesk/tickets-entetes/get`, filtersDto).pipe(catchError(error => HandleError(error))));
    }

    doGetCluTickEnteteByDoPieceInternalAction(doPieceInternal: string): Promise<any> {
        return this.doGetCluTickEntetesAction({ DO_PIECE_INTERNAL: doPieceInternal }).then(results => results.length > 0 ? results[0] : null, error => HandleError(error));
    }

    doGetFirstCluTickEnteteAction(queryParams: QueryParams_CluTickEntete): Promise<any> {
        return this.doGetCluTickEntetesAction(Object.assign({ PAGINATED: true, PAGESIZE: 1, PAGENUMBER: 1 }, queryParams)).then(results => results.length > 0 ? results[0] : null, error => HandleError(error));
    }

    doCreateCluTickEnteteAction(enteteItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-entetes/create`, enteteItem).pipe(catchError(error => HandleError(error))));
    }

    doCreateCluTickRetourEnteteAction(enteteItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-retour-entetes/create`, enteteItem).pipe(catchError(error => HandleError(error))));
    }

    doSaveCluTickEnteteAction(enteteItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-entetes/update/${enteteItem.DO_PIECE_INTERNAL}`, enteteItem).pipe(map((item: any) => {
            this.cluTicketEnteteObservable$.next(item);
            return item;
        }, catchError(error => HandleError(error)))));
    }
    // #endregion ************************************* CLU TICKETS ENTETES - END *************************************

    // #region ************************************* CLU TICKETS LIGNES *************************************
    doGetCluTickLignesAction(queryParams: QueryParams_CluTickLigne): Promise<any[]> {
        return lastValueFrom(this._httpClient.post<any[]>(`${this.tauriEndpointUrl}/api/cashdesk/tickets-lignes/get`, queryParams));
    }

    doCreateCluTickLignesAction(ligneItems: any[]): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-lignes/create`, ligneItems));
    }
    // #endregion ************************************* CLU TICKETS LIGNES - END *************************************

    // #region ************************************* CLU TICKETS REGLEMENTS *************************************
    doGetCluTickReglementsAction(queryParams: QueryParams_CluTickReglement): Promise<any[]> {
        return lastValueFrom(this._httpClient.post<any[]>(`${this.tauriEndpointUrl}/api/cashdesk/tickets-reglements/get`, queryParams).pipe(catchError(error => HandleError(error))));
    }

    doCreateCluTickReglementsAction(reglementItems: any[]): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-reglements/create`, reglementItems).pipe(catchError(error => HandleError(error))));
    }

    doSaveCluTickReglementAction(reglementItem: any): Promise<any> {
        return lastValueFrom(this._httpClient.post(`${this.tauriEndpointUrl}/api/cashdesk/tickets-reglements/update`, reglementItem).pipe(catchError(error => HandleError(error))));
    }
    // #endregion ************************************* CLU TICKETS REGLEMENTS - END *************************************

}

export class QueryParams_TickCurrentPiece {
    PAGINATED?: boolean;
    PAGESIZE?: number;
    PAGENUMBER?: number;

    SORT_COLUMN?: string;
    SORT_DIRECTION?: "ASC" | "DESC";
    DEBUG?: boolean;

    MD_SITEGID?: string;
    MD_CAISSE?: string;
    MD_CAISSE_SESSION?: string;
}

export class QueryParams_PDevice {
    PAGINATED?: boolean;
    PAGESIZE?: number;
    PAGENUMBER?: number;

    SORT_COLUMN?: string;
    SORT_DIRECTION?: "ASC" | "DESC";
    DEBUG?: boolean;

    ID?: number;
    CAISSEGID?: string;
    CAISSE_SESSION?: string;
}

export class QueryParams_CluTIckDuplicata {
    DUP_PIECE_INTERNAL?: { operator: 'eq' | 'ne', value: string } | string;
    MD_CAISSE_SESSION?: string;
    MD_SITEGID?: string;
    MD_CAISSE?: string;
    MD_CAISSIERGID?: string;
    ZSYNCUP_FLAG?: number;
    ZSYNCUP_STATUS?: string[];
    ZSYNCUP_FEEDID?: string;
    ZSYNCAGENTUP_FLAG?: number;

    PAGINATED?: boolean;
    PAGESIZE?: number;
    PAGENUMBER?: number;

    SORT_COLUMN?: string;
    SORT_DIRECTION?: "ASC" | "DESC";
    DEBUG?: boolean;
}

export class QueryParams_CluTickEntete {
    DO_PIECE_INTERNAL?: { operator: 'eq' | 'ne', value: string } | string;
    MD_CAISSE_SESSION?: string;
    ZTICKET_STATUS?: any[];
    MD_SITEGID?: string;
    MD_CAISSE?: string;
    MD_CAISSIERGID?: string;
    DO_SOLDETTC?: number;
    ZSYNCUP_FLAG?: number;
    ZSYNCUP_STATUS?: string[];
    ZSYNCUP_FEEDID?: string;
    ZSYNCAGENTUP_FLAG?: number;

    PAGINATED?: boolean;
    PAGESIZE?: number;
    PAGENUMBER?: number;

    SORT_COLUMN?: string;
    SORT_DIRECTION?: "ASC" | "DESC";
    DEBUG?: boolean;
}

export class QueryParams_CluTickLigne {
    AR_REF?: string;
    DE_NO?: number;
    MD_CAISSE_SESSION?: string;
}

export class QueryParams_CluTickReglement {
    DO_PIECE_INTERNAL?: string;
    MD_CAISSE_SESSION?: string;
    sort_column?: string;
    sort_direction?: "ASC" | "DESC";
}
